import React, { useCallback, useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import useFetch from "../hooks/useFetch";
import MainLayout from "../layouts/MainLayout";
import Dashboard from "./Dashboard";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
// import logo from '../assets/logo.png';
import AppMenu from "../components/AppMenu";
import logo from "../assets/navlogo.png";
import { motion, AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSignInAlt } from "@fortawesome/free-solid-svg-icons";

// ---------------------------------------------------------------------------
// Design tokens (see accompanying notes) — kept local to this file so the
// visual refresh doesn't require any tailwind.config.js changes.
//   bg      #05060B  near-black void
//   surface #0E1120  card base
//   violet  #7C3AED  -> cyan #22D3EE   primary gradient (the "why" spark)
//   amber   #FBBF24  franchise / opportunity accent
//   rose    #FB7185  women / community accent
// Display face: "Space Grotesk" (loaded at runtime below), body: system sans.
// ---------------------------------------------------------------------------

const FONT_LINK_ID = "think5whys-display-font";

const AUDIENCE_GROUPS = [
  {
    title: "Institutions & Enterprise",
    hue: "from-cyan-500/20 to-cyan-500/0 border-cyan-700/60 text-cyan-200",
    items: [
      "🏭 MSMEs",
      "🌾 Rural Communities",
      "🤝 SHGs",
      "🏢 Corporates",
      "💰 Financial Institutions",
      "🏛️ Governments",
      "🏗️ Contractors",
      "🌍 Global Leaders",
    ],
  },
  {
    title: "People & Families",
    hue: "from-fuchsia-500/20 to-fuchsia-500/0 border-fuchsia-700/60 text-fuchsia-200",
    items: [
      "👩‍🦰 Women",
      "🌱 Farmers",
      "🎓 Students",
      "👷‍♂️ Blue-Collar Workers",
      "👨‍👩‍👧‍👦 Families",
      "🧠 Subject Matter Experts",
      "💼 Professionals",
      "👩‍🏫 Teachers",
    ],
  },
  {
    title: "Career & Knowledge",
    hue: "from-amber-500/20 to-amber-500/0 border-amber-700/60 text-amber-200",
    items: [
      "🧑‍💼 Job Seekers",
      "✍️ Authors",
      "🏢 Publishers",
      "🏫 Schools & Colleges",
      "🎓 Universities",
      "📘 Tutorial Providers",
      "🧬 Family Trusts",
      "🤝 NGOs",
    ],
  },
];

const FEATURE_CHIPS = [
  { icon: "📱", label: "Mobile First", color: "text-cyan-200" },
  { icon: "💼", label: "Digital Hustles", color: "text-emerald-300" },
  { icon: "🏠", label: "Work From Home", color: "text-amber-300" },
  { icon: "🚺", label: "Women Empowerment", color: "text-pink-300" },
];

const FOOTER_LINKS = [
  { to: "/about-us", label: "About Us" },
  { to: "/privacy-policy", label: "Privacy Policy" },
  { to: "/terms-of-use", label: "Terms of Use" },
  { to: "/contact-us", label: "Contact Us" },
  { to: "/refund-cancellation-policy", label: "Refund & Cancellation Policy" },
];

const transformationProfiles = [
  {
    id: 1,
    icon: "🎓",
    title: "Skill & Education",
    desc: "Future-ready skills, lifelong learning, and personalised education pathways.",
    accent: "indigo",
  },
  {
    id: 2,
    icon: "🚀",
    title: "Startup & Entrepreneurship",
    desc: "End-to-end startup support — ideation, incubation, funding, mentorship & scaling.",
    accent: "teal",
  },
  {
    id: 3,
    icon: "🏭",
    title: "MSME",
    desc: "Empowering MSMEs with digital tools, credit access, market linkages & growth enablement.",
    accent: "cyan",
  },
  {
    id: 4,
    icon: "❤️",
    title: "Health",
    desc: "Health data companies, wellness, preventive care and holistic well-being for all.",
    accent: "rose",
  },
  {
    id: 5,
    icon: "🛒",
    title: "Local Marketplace",
    desc: "Vocal for Local — connecting neighborhood businesses with local buyers.",
    accent: "orange",
  },
  {
    id: 6,
    icon: "💼",
    title: "Youth Employment",
    desc: "Bridging youth with jobs, gigs, apprenticeships and career opportunities.",
    accent: "blue",
  },
  {
    id: 7,
    icon: "👩‍👩‍👧",
    title: "SHGs Ecosystem",
    desc: "Strengthening Self Help Groups with digital tools, training, finance and markets.",
    accent: "pink",
  },
  {
    id: 8,
    icon: "🌍",
    title: "International Trade",
    desc: "Enabling global trade opportunities for businesses of all sizes.",
    accent: "sky",
  },
  {
    id: 9,
    icon: "💡",
    title: "Innovation Management",
    desc: "Driving innovation culture through ideation, R&D, IP management and collaboration.",
    accent: "yellow",
  },
  {
    id: 10,
    icon: "👥",
    title: "Hustlers Ecosystem",
    desc: "Empowering hustlers, creators & solopreneurs to grow, earn and lead.",
    accent: "violet",
  },
  {
    id: 11,
    icon: "📈",
    title: "Digital Economy Catalyst",
    desc: "Accelerating digital adoption, fintech, e-commerce and emerging technologies.",
    accent: "amber",
  },
  {
    id: 12,
    icon: "🧠",
    title: "Causal Thinking",
    desc: "Causal insights for better decisions, problem solving and lasting impact.",
    accent: "emerald",
  },
];

const accentMap = {
  indigo: {
    border: "hover:border-indigo-400",
    ring: "border-indigo-400 bg-indigo-500/10",
    badge: "bg-indigo-500",
  },
  teal: {
    border: "hover:border-teal-400",
    ring: "border-teal-400 bg-teal-500/10",
    badge: "bg-teal-500",
  },
  cyan: {
    border: "hover:border-cyan-400",
    ring: "border-cyan-400 bg-cyan-500/10",
    badge: "bg-cyan-500",
  },
  rose: {
    border: "hover:border-rose-400",
    ring: "border-rose-400 bg-rose-500/10",
    badge: "bg-rose-500",
  },
  orange: {
    border: "hover:border-orange-400",
    ring: "border-orange-400 bg-orange-500/10",
    badge: "bg-orange-500",
  },
  blue: {
    border: "hover:border-blue-400",
    ring: "border-blue-400 bg-blue-500/10",
    badge: "bg-blue-500",
  },
  pink: {
    border: "hover:border-pink-400",
    ring: "border-pink-400 bg-pink-500/10",
    badge: "bg-pink-500",
  },
  sky: {
    border: "hover:border-sky-400",
    ring: "border-sky-400 bg-sky-500/10",
    badge: "bg-sky-500",
  },
  yellow: {
    border: "hover:border-yellow-400",
    ring: "border-yellow-400 bg-yellow-500/10",
    badge: "bg-yellow-500",
  },
  violet: {
    border: "hover:border-violet-400",
    ring: "border-violet-400 bg-violet-500/10",
    badge: "bg-violet-500",
  },
  amber: {
    border: "hover:border-amber-400",
    ring: "border-amber-400 bg-amber-500/10",
    badge: "bg-amber-500",
  },
  emerald: {
    border: "hover:border-emerald-400",
    ring: "border-emerald-400 bg-emerald-500/10",
    badge: "bg-emerald-500",
  },
};

const pillars = [
  {
    title: "MSMEs",
    icon: "🏭",
    desc: "Digital tools, credit access & growth enablement.",
    accent: "hover:border-cyan-400 hover:bg-cyan-500/10",
  },
  {
    title: "Women",
    icon: "👩",
    desc: "Equal opportunity, safety & economic independence.",
    accent: "hover:border-pink-400 hover:bg-pink-500/10",
  },
  {
    title: "Youth",
    icon: "🎓",
    desc: "Skills, jobs, gigs & career pathways.",
    accent: "hover:border-violet-400 hover:bg-violet-500/10",
  },
  {
    title: "Agriculture",
    icon: "🌾",
    desc: "Farmer-first tools, market access & income growth.",
    accent: "hover:border-green-400 hover:bg-green-500/10",
  },
  {
    title: "Blue Collar Workers",
    icon: "👷",
    desc: "Dignity of work, fair wages & upskilling.",
    accent: "hover:border-amber-400 hover:bg-amber-500/10",
  },
  {
    title: "Learning & Development",
    icon: "📚",
    desc: "Lifelong learning for every stage of life.",
    accent: "hover:border-indigo-400 hover:bg-indigo-500/10",
  },
  {
    title: "Governance",
    icon: "🏛️",
    desc: "Transparent, tech-enabled public systems.",
    accent: "hover:border-emerald-400 hover:bg-emerald-500/10",
  },
  {
    title: "Healthcare",
    icon: "❤️",
    desc: "Preventive care & holistic well-being for all.",
    accent: "hover:border-red-400 hover:bg-red-500/10",
  },
];

const Home = () => {
  const authState = useSelector((state) => state.authReducer);
  const [showIdsInfo, setShowIdsInfo] = useState(false);
  const [showProfiles, setShowProfiles] = useState(false);
  const [showPillars, setShowPillars] = useState(false);
  const [selectedProfiles, setSelectedProfiles] = useState([]);

  const toggleProfile = (id) => {
    setSelectedProfiles((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id],
    );
  };

  const { isLoggedIn } = authState;

  useEffect(() => {
    document.title = isLoggedIn
      ? `${authState.user.name}'s Dashboard`
      : "Think5Whys";
  }, [authState, isLoggedIn]);

  // Load the display typeface once, at runtime, so no build config needs to change.
  useEffect(() => {
    if (document.getElementById(FONT_LINK_ID)) return;
    const link = document.createElement("link");
    link.id = FONT_LINK_ID;
    link.rel = "stylesheet";
    link.href =
      "https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;700&display=swap";
    document.head.appendChild(link);
  }, []);

  const [fetchData, { loading }] = useFetch();
  const navigate = useNavigate();

  const dispatch = useDispatch();
  const [isNavbarOpen, setIsNavbarOpen] = useState(false);

  const toggleNavbar = () => {
    setIsNavbarOpen(!isNavbarOpen);
  };

  const [logoutConfirm, setLogoutConfirm] = useState(false);
  const handleLogoutClick = () => {
    setLogoutConfirm(true);
  };

  const handleConfirmLogoutClick = () => {
    dispatch(logout());
    setLogoutConfirm(false);
  };

  const handleCancelLogoutClick = () => {
    setLogoutConfirm(false);
  };

  const handleHomeClicked = () => {
    navigate("/my-profile-home");
  };

  const handleDashboardClicked = () => {
    navigate("/dashboard");
  };

  const handleAdminDashboardClicked = () => {
    navigate("/admindashboard");
  };

  const handleAboutThink5WhysClicked = () => {
    navigate("/about-think5whys-home");
  };

  // Inportant: This code is temporarirly commented to land user directly on PurpleDoor

  // const fetchUserLanding = useCallback(() => {
  //   const config = {
  //     url: "/userlanding",
  //     method: "get",
  //     headers: { Authorization: authState.token },
  //   };
  //   fetchData(config, { showSuccessToast: false })
  //     .then((response) => {
  //       if (!response || Object.keys(response).length === 0) {
  //         console.error("No user landing data found in response");
  //         return;
  //       }
  //       const { hasAppLike, moduleHomeRoute, hasCustomDashboard } = response;
  //       if (hasAppLike === 1) {
  //         navigate(moduleHomeRoute);
  //       } else {
  //         navigate(hasCustomDashboard === 1 ? "/dashboard" : "/purpledoor");
  //       }
  //     })
  //     .catch((error) => {
  //       console.error("Error fetching user landing data:", error);
  //     });
  // }, [authState.token, fetchData, navigate]);

  const fetchUserLanding = useCallback(() => {
    navigate("/purpledoor");
  }, [navigate]);

  useEffect(() => {
    if (isLoggedIn) fetchUserLanding();
  }, [isLoggedIn, fetchUserLanding]);

  const displayFont = {
    fontFamily: "'Space Grotesk', ui-sans-serif, system-ui, sans-serif",
  };

  return (
    <div className="bg-[#05060B]">
      <div className="sticky top-0 z-40 flex flex-row items-center justify-between border-b border-white/10 bg-[#05060B]/80 backdrop-blur-md px-3 py-2">
        <div className="flex items-center gap-2">
          <Link to="/" className="shrink-0">
            <motion.img
              src={logo}
              alt="Logo"
              style={{ width: 36 }}
              className="rounded-md"
              animate={{ y: [0, -4, 0] }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            />
          </Link>
          <div>
            <Link
              to="/"
              className="flex items-baseline gap-1 text-transparent bg-clip-text bg-gradient-to-r from-violet-300 via-fuchsia-200 to-amber-200 uppercase text-xs lg:text-sm font-bold tracking-wide"
              style={displayFont}
            >
              Think5Whys
              {/* <sup>
                <span className="text-[7px] text-white/60 normal-case font-normal tracking-normal">
                  Beta
                </span>
              </sup> */}
            </Link>
            <p className="text-white/50 text-[11px] leading-tight">
              {authState.user && authState.user.name
                ? authState.user.name
                : "Welcome Guest"}
            </p>
          </div>
        </div>
        {!authState.isLoggedIn && (
          <Link
            to="/login"
            className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-emerald-600 to-emerald-500 w-fit px-3 py-1.5 text-white text-xs lg:text-sm font-medium shadow-lg shadow-emerald-900/40 transition hover:shadow-emerald-700/50 hover:from-emerald-500 hover:to-emerald-400"
          >
            <FontAwesomeIcon
              icon={faSignInAlt}
              className="text-xs group-hover:translate-x-0.5 transition-transform"
            />
            Login
          </Link>
        )}
      </div>

      {!isLoggedIn ? (
        <div className="min-h-[90vh] bg-[#05060B] text-white overflow-hidden">
          <section className="relative isolate overflow-hidden border-b border-white/10">
            <div className="pointer-events-none absolute left-1/2 top-10 -z-10 h-64 w-64 -translate-x-1/2 opacity-40 lg:h-96 lg:w-96">
              {[0, 1, 2, 3, 4].map((ring) => (
                <motion.span
                  key={ring}
                  className="absolute inset-0 rounded-full border border-cyan-400/30"
                  style={{ margin: ring * 18 }}
                  animate={{ scale: [1, 1.06, 1], opacity: [0.5, 0.15, 0.5] }}
                  transition={{
                    duration: 5,
                    repeat: Infinity,
                    delay: ring * 0.4,
                    ease: "easeInOut",
                  }}
                />
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: "easeOut" }}
              className="relative mx-auto flex max-w-3xl flex-col items-center px-4 pt-4 pb-3 text-center lg:pt-6 lg:pb-5"
            >
              <span className="mb-1 inline-flex items-center gap-1 rounded-full border border-amber-400/40 bg-amber-400/10 px-3 py-1 text-[10px] lg:text-xs font-semibold text-amber-300">
                🚀 Your Own TaaP: Transformation as a Platform
              </span>
              <h1
                className="text-xl lg:text-2xl font-bold leading-[1.05] bg-clip-text text-transparent bg-gradient-to-br from-white via-white to-cyan-200"
                style={displayFont}
              >
                Build Digital Future with Young Gen
              </h1>
              <h2
                className="text-lg lg:text-xl font-bold leading-[1.05] bg-clip-text text-transparent bg-gradient-to-br from-orange-300 via-white to-green-600"
                style={displayFont}
              >
                An Evolving Platform. Made In India. Made For The World!
              </h2>
              <p className="mx-auto mt-1.5 max-w-2xl text-center text-[11px] font-medium text-gray-500 lg:text-xs">
                <span className="text-orange-300">
                  900M+ smartphone users in India
                </span>
                <span className="mx-1.5 text-gray-600">·</span>
                <span className="text-cyan-300">🌍 5B+ worldwide</span>
                <span className="mx-1.5 text-gray-600">·</span>
                <span className="text-gray-400">and growing every day</span>
              </p>

              <div className="mx-auto mt-6 max-w-5xl px-3">
                <div className="flex flex-wrap items-center justify-center gap-3">
                  <button
                    onClick={() => setShowProfiles((prev) => !prev)}
                    className="inline-flex items-center gap-1.5 rounded-full border border-fuchsia-500/30 bg-slate-900/80 w-fit px-4 py-2 shadow-md transition hover:border-fuchsia-400 hover:bg-fuchsia-950/40"
                  >
                    <span
                      className="text-xs lg:text-sm font-bold bg-clip-text text-transparent bg-gradient-to-br from-cyan-200 via-yellow-200 to-purple-300"
                      style={displayFont}
                    >
                      12 Transformation Profiles
                    </span>
                    <span
                      className={`text-cyan-200 text-[12px] transition-transform duration-300 ${
                        showProfiles ? "rotate-180" : ""
                      }`}
                    >
                      ▾
                    </span>
                  </button>

                  <button
                    onClick={() => setShowPillars((prev) => !prev)}
                    className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-slate-900/80 w-fit px-4 py-2 shadow-md transition hover:border-emerald-400 hover:bg-emerald-950/30"
                  >
                    <span className="text-xs lg:text-sm font-semibold text-gray-300">
                      Across 8 Pillars
                    </span>
                    <span
                      className={`text-gray-400 text-[12px] transition-transform duration-300 ${
                        showPillars ? "rotate-180" : ""
                      }`}
                    >
                      ▾
                    </span>
                  </button>
                </div>

                <AnimatePresence>
                  {showProfiles && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.35, ease: "easeInOut" }}
                      className="overflow-y-hidden"
                    >
                      <h2
                        className="text-center text-lg lg:text-xl font-extrabold leading-tight bg-clip-text text-transparent bg-gradient-to-br from-cyan-200 via-yellow-200 to-fuchsia-300"
                        style={displayFont}
                      >
                        12 Transformational Profiles Everybody Belongs To
                      </h2>
                      <p className="mx-auto mt-2 max-w-xl text-center text-xs text-gray-400 lg:text-sm">
                        Every one of us belongs to one or more of these. Choose
                        the profiles that describe you, and grow with
                        Think5Whys.
                      </p>

                      <div className="mt-4 grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-3 lg:gap-3 min-w-0">
                        {transformationProfiles.map((p) => {
                          const isSelected = selectedProfiles.includes(p.id);
                          const accent = accentMap[p.accent];
                          return (
                            <button
                              key={p.id}
                              onClick={() => toggleProfile(p.id)}
                              className={`group relative flex w-full min-w-0 flex-col items-start rounded-xl border p-3 text-left transition duration-200 ${
                                isSelected
                                  ? accent.ring
                                  : `border-white/10 bg-white/5 ${accent.border} hover:bg-white/[0.08]`
                              }`}
                            >
                              <span
                                className={`absolute top-2 left-2 flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold text-white shadow-md ${accent.badge}`}
                              >
                                {p.id}
                              </span>
                              {isSelected && (
                                <span className="absolute top-2 right-2 text-xs text-emerald-300">
                                  ✓
                                </span>
                              )}
                              <span className="text-xl">{p.icon}</span>
                              {/* <span className="mt-1.5 text-xs font-bold text-white lg:text-sm">
                                {p.title}
                              </span>
                              <span className="mt-1 text-[10px] leading-snug text-gray-400 lg:text-xs">
                                {p.desc}
                              </span> */}
                              <span className="mt-1.5 text-xs font-bold text-white lg:text-sm break-words">
                                {p.title}
                              </span>
                              <span className="mt-1 text-[10px] leading-snug text-gray-400 lg:text-xs break-words">
                                {p.desc}
                              </span>
                            </button>
                          );
                        })}
                      </div>

                      {selectedProfiles.length > 0 && (
                        <p className="mt-3 text-center text-[11px] text-emerald-300 lg:text-xs">
                          {selectedProfiles.length} profile
                          {selectedProfiles.length > 1 ? "s" : ""} selected —
                          we'll tailor your journey around{" "}
                          {selectedProfiles.length > 1 ? "these" : "this"}.
                        </p>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>

                <AnimatePresence>
                  {showPillars && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.35, ease: "easeInOut" }}
                      className="overflow-hidden"
                    >
                      <h2
                        className="text-center text-xl lg:text-3xl font-extrabold leading-tight bg-clip-text text-transparent bg-gradient-to-br from-cyan-200 via-yellow-200 to-fuchsia-300"
                        style={displayFont}
                      >
                        Support 8 Pillars
                      </h2>
                      <div className="mt-4 grid grid-cols-2 gap-2.5 sm:grid-cols-4 lg:gap-3">
                        {pillars.map((pl) => (
                          <div
                            key={pl.title}
                            title={pl.title}
                            className={`flex flex-col items-start rounded-xl border border-white/10 bg-white/5 p-3 text-left transition duration-200 ${pl.accent}`}
                          >
                            <span className="text-xl">{pl.icon}</span>
                            <span className="mt-1.5 text-xs font-bold text-white lg:text-sm">
                              {pl.title}
                            </span>
                            <span className="mt-1 text-[10px] leading-snug text-gray-400 lg:text-xs">
                              {pl.desc}
                            </span>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <Link
                to="/signup"
                className="mt-6 inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-violet-600 via-fuchsia-600 to-cyan-500 px-5 py-2.5 text-xs lg:text-sm font-semibold text-white shadow-xl shadow-violet-900/40 transition duration-300 hover:shadow-cyan-700/40 hover:-translate-y-0.5"
              >
                📝 Create New User Account
              </Link>

              <a
                href="https://www.youtube.com/@think5whys"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 inline-flex items-center gap-2 rounded-xl border border-cyan-400/40 bg-white/5 px-4 py-2 text-xs lg:text-sm font-semibold text-cyan-200 shadow-md backdrop-blur-sm transition duration-300 hover:border-cyan-300 hover:bg-cyan-400/10 hover:-translate-y-0.5"
              >
                ▶️ View Self Onboarding Video
              </a>
            </motion.div>
          </section>

          {/* Quick Links */}
          <div className="flex flex-wrap items-center justify-center gap-2 px-3 pt-3 sm:gap-3 sm:px-5">
            <Link
              to="/genz/udaan/flywheel"
              className="inline-flex items-center gap-1.5 rounded-full border border-fuchsia-500/30 bg-slate-900/80 px-3 py-1.5 text-xs font-semibold text-fuchsia-200 shadow-md transition hover:border-fuchsia-400 hover:bg-fuchsia-950/40 sm:px-4 sm:py-2 sm:text-sm"
            >
              🚀 GenZUdaan Flywheel
            </Link>

            <a
              href="https://www.youtube.com/@think5whys"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-full border border-red-500/30 bg-slate-900/80 px-3 py-1.5 text-xs font-semibold text-red-200 shadow-md transition hover:border-red-400 hover:bg-red-950/40 sm:px-4 sm:py-2 sm:text-sm"
            >
              ▶️ Think5Whys YouTube Channel
            </a>

            <a
              href="https://www.youtube.com/@foundherindia"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-full border border-violet-500/30 bg-slate-900/80 px-3 py-1.5 text-xs font-semibold text-violet-200 shadow-md transition hover:border-violet-400 hover:bg-violet-950/40 sm:px-4 sm:py-2 sm:text-sm"
            >
              💜 FoundHer India YouTube Channel
            </a>
            <button
              onClick={() => setShowIdsInfo((prev) => !prev)}
              className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/30 bg-slate-900/80 w-fit px-3 py-1.5 text-xs font-semibold text-emerald-200 shadow-md transition hover:border-emerald-400 hover:bg-emerald-950/40 sm:px-4 sm:py-2 sm:text-sm"
            >
              🎙️ Your Language. Create. Publish. Earn. Repeat.
            </button>
          </div>

          {showIdsInfo && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              className="mx-auto mt-3 max-w-xl rounded-xl border border-emerald-400/30 bg-slate-900/90 px-5 py-4 text-center shadow-lg backdrop-blur-sm"
            >
              <h4 className="text-sm font-bold text-emerald-200 lg:text-base">
                IDS: Inclusive Digital Sustenance
              </h4>
              <p className="mt-1.5 text-xs leading-relaxed text-gray-300 lg:text-sm">
                Think5Whys is a collaborative ecosystem, not an app. Discovered
                something useful while exploring our hundreds of features? Turn
                it into a video, publish it on YouTube or any social platform —
                in the language of your choice — and earn income*.
              </p>
              <p className="mt-1.5 text-xs leading-relaxed text-gray-400 lg:text-sm">
                May use AI to create and edit your content, making it sharper
                and more watchable.
              </p>
              <Link
                to="/purpledoor/ids"
                className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-emerald-500 to-cyan-500 px-4 py-1.5 text-xs font-semibold text-white shadow-md transition hover:-translate-y-0.5 hover:shadow-emerald-700/40"
              >
                Explore IDS →
              </Link>
              <p className="mt-1.5 text-[10px] text-yellow-100">
                *Income potential varies by content and engagement.
              </p>
            </motion.div>
          )}

          {/* --------------------------------------------------------------- */}
          {/* Build Your Digital Future — feature panel                       */}
          {/* --------------------------------------------------------------- */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6 }}
            className="mx-auto mt-2 max-w-5xl px-4"
          >
            <div className="rounded-2xl border border-violet-700/40 bg-gradient-to-br from-violet-950/60 via-[#0E1120] to-cyan-950/40 p-5 lg:p-8 shadow-2xl shadow-violet-950/40">
              <h2
                className="text-center text-sm lg:text-lg font-bold text-cyan-200"
                style={displayFont}
              >
                Build Your Digital Future with Think5Whys
              </h2>
              <div className="mt-5 rounded-xl border border-amber-500/30 bg-amber-500/10 px-3 py-3 text-center">
                <p className="text-[11px] lg:text-sm font-semibold text-amber-200">
                  🎯 Target: 1 Lakh Digital Franchises • 1 Lakh FoundHer (Gen Z
                  Women Entreprenure / SHG) • Year 2026 & 27
                </p>
                <p className="mt-1 text-[10px] lg:text-xs text-gray-300">
                  Zero Experience • Full Support • Working Capital Credits •
                  Growth Pathways • Rewards & Contests
                </p>
              </div>
              <p className="mx-auto mt-3 max-w-2xl text-center text-xs lg:text-sm text-gray-300 leading-relaxed">
                Transform your{" "}
                <span className="text-violet-300 font-semibold">
                  mobile phone
                </span>
                ,<span className="text-cyan-300 font-semibold"> skills</span>,
                and
                <span className="text-pink-300 font-semibold">
                  {" "}
                  ambition
                </span>{" "}
                into real-world income, entrepreneurship, learning, rewards, and
                growth opportunities.
              </p>

              <div className="mt-5 grid grid-cols-2 gap-2 lg:grid-cols-4 lg:gap-3">
                {FEATURE_CHIPS.map((chip) => (
                  <div
                    key={chip.label}
                    className="group rounded-xl border border-white/10 bg-black/40 px-2 py-3 text-center transition duration-300 hover:border-white/30 hover:bg-black/60"
                  >
                    <p className="text-lg lg:text-xl transition-transform duration-300 group-hover:scale-110">
                      {chip.icon}
                    </p>
                    <p
                      className={`mt-1 text-[10px] lg:text-xs font-semibold ${chip.color}`}
                    >
                      {chip.label}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </motion.section>

          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mx-auto mt-2 max-w-6xl overflow-hidden rounded-3xl"
          >
            <img
              src="/assets/seedinvestorbanner.webp"
              alt="Think5Whys seed investors backing the TaaP platform"
              className="block w-full h-auto"
            />
          </motion.section>

          {/* --------------------------------------------------------------- */}
          {/* Audience grid                                                    */}
          {/* --------------------------------------------------------------- */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mx-auto mt-2 max-w-6xl px-4 pb-4"
          >
            <h3 className="mb-3 text-center text-[11px] lg:text-sm font-semibold uppercase tracking-widest text-white/40">
              Built for everyone in the ecosystem
            </h3>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              {AUDIENCE_GROUPS.map((group) => (
                <div
                  key={group.title}
                  className={`rounded-2xl border bg-gradient-to-b p-3 lg:p-4 transition duration-300 hover:-translate-y-1 hover:shadow-lg ${group.hue}`}
                >
                  <p
                    className={`mb-2 text-[10px] lg:text-xs font-bold uppercase tracking-wide ${group.hue.split(" ").pop()}`}
                  >
                    {group.title}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {group.items.map((item) => (
                      <span
                        key={item}
                        className="rounded-full bg-black/40 border border-white/10 px-2 py-1 text-[10px] lg:text-xs text-white/90"
                      >
                        {item}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </motion.section>

          {/* --------------------------------------------------------------- */}
          {/* Footer                                                           */}
          {/* --------------------------------------------------------------- */}
          <footer className="mt-2 border-t border-white/10 bg-[#05060B] py-5 text-white">
            <div className="mx-auto flex flex-col items-center justify-between gap-3 px-3">
              <div className="text-[10px] lg:text-xs text-amber-200/90 text-center">
                © {new Date().getFullYear()} Think5Whys Global DeepTech Private
                Limited, DPIIT (Govt. of India) Recognised Tech Startup
              </div>
              <div className="flex flex-row flex-wrap justify-center gap-x-3 gap-y-1 text-xs lg:text-sm">
                {FOOTER_LINKS.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className="text-[10px] text-white/70 hover:text-amber-200 transition-colors"
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
              <div className="mt-1 text-center text-xs">
                <p className="text-[10px] text-gray-500">
                  Address: B-1402, Riviera, Casa-Bella, Palava, Kalyan-Shil
                  Road, Dombivali (East), Thane, Mumbai Metropolitan Region,
                  Maharashtra, India, Postal Code: 421204
                </p>
              </div>
            </div>
          </footer>
        </div>
      ) : (
        <Dashboard />
      )}
    </div>
  );
};

export default Home;
